import * as _ from "lodash";
import { inject, observer } from "mobx-react";
import { observe, when } from "mobx";

import { Button, Col, Dropdown, Form, Modal, NavDropdown, Row } from "react-bootstrap";
import { parse } from "query-string";
import * as React from "react";
import { ChangeEvent, MouseEvent } from "react";

import Auth from "../../common/auth/auth";
import { Utils } from "@/common/utils";
import { AppStore, DashboardStore, FeatureGatingStore, FilterStore, TicketStore } from "../../stores";
import { Dashboard as IDashboard, DashboardExport } from "../../stores/dashboardStore";
import { Task } from "Stores/dashboardStore/tickets";
import { TableauDisplay } from "../Common/TableauDisplay";
import { TicketDetailForm } from "../Tickets/DetailForm";
import { TicketList } from "../Tickets/List";
import { Location, NavigateFunction, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import { SelectWrapper } from "Components/Common/SelectWrapper";
import { VerticalResize } from "Components/Icons/Icons";
import PageTitle from "Components/Common/PageTitle";
import { DashboardQueries } from "@/common/dashboards";
import { SchoolSettings, useSchoolSettings } from "@/hooks/schools";
import { ArrowLeft, ChevronDown, ChevronUp, Download, Eye, ImageIcon, Plus, Table } from "lucide-react";
import { CrosstabFileFormat } from "@tableau/embedding-api";

function useQuery() {
    const { search } = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
}

const tableauVizOptions = {
    hideTabs: true,
    hideToolbar: true,
};

type DashboardPageParams = {
    dashboardName: string;
    ticketId: string;
};

export function DashboardPage() {
    const location = useLocation();
    const query = useQuery();
    const params = useParams<DashboardPageParams>();
    const navigate = useNavigate();
    const { data: schoolSettings } = useSchoolSettings();

    if (!params.dashboardName) {
        return null;
    }
    return <Dashboard dashboardName={params.dashboardName} ticketId={params.ticketId} viewId={query.get("viewId")} refreshView={query.get("refreshView")} location={location} schoolSettings={schoolSettings} navigate={navigate} />;
}

interface IDashboardProps {
    dashboardStore?: DashboardStore;
    ticketStore?: TicketStore;
    filterStore?: FilterStore;
    appStore?: AppStore;
    dashboardName: string;
    ticketId?: string;
    viewId?: string | null;
    refreshView?: string | null;
    location: Location;
    featureGatingStore?: FeatureGatingStore;
    navigate: NavigateFunction;
    schoolSettings?: SchoolSettings;
}

interface IDashboardState {
    shouldShowActionplanForm: boolean;
    shouldShowSnapshotModal: boolean;
    shouldShowExportModal: boolean;
    shouldEnableTicketCreation: boolean;
    shouldSaveView: boolean;
    taskToFollowUp?: Partial<Task>;
    ticketId?: string;
    viewId?: string | null;
    refreshView?: string | null;
    dashboardName: string;
    dashboardId: number;
    overrideSheet?: string;
    vizOptions?: object;
    queryParameters?: object;
    dashboard?: IDashboard;
    vizDidLoad: boolean;
    dashboardTab?: string;
    exportList: DashboardExport[];
    selectedExport?: string | DashboardExport;
    selectedExportType?: string;
    dashboardSize: number;
    showDropdown?: boolean;
}

@inject("dashboardStore", "filterStore", "ticketStore", "appStore", "featureGatingStore")
@observer
export class Dashboard extends React.Component<IDashboardProps, IDashboardState> {
    constructor(props: any) {
        super(props);
        this.ticketDetailFormRef = React.createRef();
        this.ticketListFormRef = React.createRef();
        this.state = {
            shouldShowActionplanForm: false,
            shouldShowSnapshotModal: false,
            shouldShowExportModal: false,
            shouldEnableTicketCreation: false,
            shouldSaveView: false,
            taskToFollowUp: undefined,
            ticketId: "",
            viewId: this.props.viewId,
            refreshView: this.props.refreshView,
            dashboardName: "",
            dashboardId: 0,
            overrideSheet: undefined,
            vizOptions: undefined,
            queryParameters: undefined,
            vizDidLoad: false,
            dashboardTab: undefined,
            exportList: [],
            selectedExport: undefined,
            selectedExportType: "excel",
            dashboardSize: 550,
            showDropdown: false,
        };
    }

    auth?: Auth;
    vizDidLoad: boolean;
    vizIsComplete: boolean;
    child?: TableauDisplay;
    ticketDetailFormRef: any;
    ticketListFormRef: any;

    ensureAuth() {
        if (this.vizIsComplete && !this.vizDidLoad && this.auth) {
            this.auth.loginRedirect();
        }
    }

    setViewName = () => {
        Utils.setViewName("DashboardView-" + String(this.props.dashboardName), this.props.dashboardStore!.dashboard ? this.props.dashboardStore!.dashboard.name : undefined);
    };

    handleVizLoaded = (activeSheetName: string) => {
        this.vizDidLoad = true;
        this.vizIsComplete = true;
        this.setState({ vizDidLoad: true, dashboardTab: activeSheetName, shouldEnableTicketCreation: true });
        setTimeout(() => this.ensureAuth(), 3000);
        when(
            () => this.props.dashboardStore!.dashboardExports.length > 0,
            () => {
                const exportList = this._filterExportList(activeSheetName);
                let selectedExport;
                if (exportList.length > 0) {
                    selectedExport = exportList[0];
                }
                this.setState({ exportList: exportList, selectedExport: selectedExport });
            }
        );
    };

    handleTabSwitch = (newSheetName: string) => {
        const exportList = this._filterExportList(newSheetName);
        let selectedExport;
        if (exportList.length > 0) {
            selectedExport = exportList[0];
        }
        this.setState({ dashboardTab: newSheetName, exportList: exportList, selectedExport: selectedExport });
    };

    handleFollowUpTask = (task: Task) => {
        if (!this.props.ticketStore) return;
        const currentUser = Utils.getCurrentUser();
        if (!currentUser) {
            // lame message for a very fringe scenario. See GLobal UI Enhancement docs for discussion of error displays.
            alert("You can't create a follow up for this task, because it can't be determined who the current user is.");
            return;
        }
        const isOwner = currentUser.email === task.created_by;
        if (isOwner) {
            this.setState({
                shouldShowActionplanForm: true,
                taskToFollowUp: task,
            });
        }
    };

    resetUrlParamsAndInstaOpen = () => {
        this.setState({
            ticketId: undefined,
        });
        const url = Utils.getLinkBase();
        this.props.navigate(url + "/dashboard/" + this.props.dashboardName);
    };

    handleShowForm = () => {
        this.resetUrlParamsAndInstaOpen();
        this.setState({
            shouldShowSnapshotModal: false,
            shouldShowActionplanForm: true,
        });
    };

    handleCloseSnapshotModal = () => {
        this.setState({
            shouldShowSnapshotModal: false,
        });
    };

    showLists = () => {
        if (this.state.shouldShowActionplanForm) {
            this.setState(
                {
                    shouldShowActionplanForm: false,
                },
                () => {
                    let fetchCount = 0;
                    const disposer = observe(this.props.ticketStore!, "isFetchingTickets", (change: any) => {
                        fetchCount++;
                        if (change.newValue === false && fetchCount >= 2) {
                            disposer();
                            this.scrollToList();
                        }
                    });
                }
            );
        } else {
            this.scrollToList();
        }
    };

    downloadExport = () => {
        if (!this.child || !this.state.selectedExport) return;
        if (typeof this.state.selectedExport === "string") return;
        this.setState({ shouldShowExportModal: false });
        this.child.handleExport(this.state.selectedExport.sheet, this.state.selectedExportType === "excel" ? CrosstabFileFormat.Excel : CrosstabFileFormat.CSV);
    };

    scrollToList = () => {
        scroll(0, this.ticketListFormRef.current.offsetTop);
    };

    scrollToCreate = () => {
        scroll(0, this.ticketDetailFormRef.current.offsetTop);
    };

    handleShowSnapshotModal = () => {
        if (this.state.dashboard && this.state.dashboard.dashboard_type === "BYODashboard") {
            this.handleShowForm();
            setTimeout(() => this.handleSnapshotModalDismissed());
            return null;
        }
        this.resetUrlParamsAndInstaOpen();
        this.setState({
            shouldShowSnapshotModal: true,
            shouldShowActionplanForm: true,
        });
        setTimeout(() => this.handleSnapshotModalDismissed());
    };

    exportActionPlans = () => {
        this.props.dashboardStore?.exportActionPlan();
    };

    handleSnapshotModalDismissed = () => {
        if (this.state.shouldShowActionplanForm) {
            this.scrollToCreate();
        }
    };

    handleShowExportModal = () => {
        this.setState({ shouldShowExportModal: true });
    };

    handleExportDashboardPDF = () => {
        this.child?.handleExportPDF();
    };

    handleExportDashboardPNG = () => {
        this.child?.handleExportDashboardPNG();
    };

    handleCloseExportModal = () => {
        this.setState({ shouldShowExportModal: false });
    };

    static getDerivedStateFromProps(props, state) {
        const nextDashboardName = props.dashboardName;
        let nextState = {};
        if (!state || nextDashboardName !== state.dashboardName) {
            nextState = {
                dashboardName: nextDashboardName,
                shouldShowActionplanForm: false,
                shouldEnableTicketCreation: false,
            };
        }

        if (!state || props.ticketId !== state.ticketId) {
            nextState = { ...nextState, ticketId: props.ticketId };
        }

        if (!state || props.viewId !== state.viewId) {
            nextState = { ...nextState, viewId: props.viewId };
        }

        if (!state || props.refreshView !== state.refreshView) {
            nextState = { ...nextState, refreshView: props.refreshView };
        }
        return nextState;
    }

    async componentDidMount() {
        this.auth = new Auth();

        const queryParameters = parse(this.props.location.search);
        if (queryParameters.override_sheet) {
            this.setState({ overrideSheet: queryParameters["override_sheet"] as string });
            delete queryParameters.override_sheet;
        }
        // tab is an alias for override_sheet
        if (queryParameters.tab) {
            this.setState({ overrideSheet: queryParameters["tab"] as string });
            delete queryParameters.tab;
        }

        this.setState({ vizOptions: { ...tableauVizOptions }, queryParameters: { ...queryParameters } });

        const callbackOnError = (/* msg: string */) => {
            const oncloseerror = () => {
                if (this.props.ticketId) {
                    const url = Utils.getLinkBase() + "/actionplans/" + this.props.ticketId;
                    this.props.navigate(url);
                } else {
                    const url = Utils.getLinkBase();
                    this.props.navigate(url);
                }
            };

            const errorMessage = this.props.ticketId ? "Could not access a dashboard with that ID. If you feel you have received this message in error, please contact your administrator. Click close to be forwarded to the ActionPlan" : "Could not access a dashboard with that ID. If you feel you have received this message in error, please contact your administrator. Click close to be forwarded to the dashboard list.";

            this.props.appStore!.addError(new Error(), errorMessage, oncloseerror);
        };
        await this.props.dashboardStore!.getDashboardById(this.props.dashboardName, undefined, callbackOnError);
        if (this.props.dashboardStore) {
            const dashboard = this.props.dashboardStore.dashboard;
            if (Number.isInteger(Number(this.props.dashboardName)) && dashboard) {
                // replace current URL with the one that has the dashboard name
                const url = Utils.getActionplanUrl(Utils.getUrlFriendlyString(dashboard.name), this.props.ticketId);
                const params: Record<string, string> = {};
                if (this.props.viewId) {
                    params["viewId"] = this.state.viewId as string;
                }
                if (this.props.refreshView) {
                    params.refreshView = this.state.refreshView as string;
                }

                const searchParams = new URLSearchParams(params);

                this.props.navigate(url + "?" + searchParams.toString(), { replace: true });
            }
            if (dashboard && dashboard.dashboard_type === "BYODashboard") {
                this.setState({ dashboard, dashboardId: dashboard.id, shouldEnableTicketCreation: true });
                return null;
            }
            if (dashboard && dashboard.dashboard_type === "enflux_dashboard") {
                this.setState({ dashboardSize: 770 });
            }
            this.setState({ dashboard, dashboardId: dashboard?.id || 0 });
        }
        this.setViewName();
    }

    async componentDidUpdate(prevProps: Readonly<IDashboardProps>, prevState: Readonly<IDashboardState>) {
        const callbackOnError = (/* msg: string */) => {
            const oncloseerror = () => {
                if (this.props.ticketId) {
                    const url = Utils.getLinkBase() + "/actionplans/" + this.props.ticketId;
                    this.props.navigate(url);
                } else {
                    const url = Utils.getLinkBase();
                    this.props.navigate(url);
                }
            };

            const errorMessage = this.props.ticketId ? "Could not access a dashboard with that ID. If you feel you have received this message in error, please contact your administrator. Click close to be forwarded to the ActionPlan" : "Could not access a dashboard with that ID. If you feel you have received this message in error, please contact your administrator. Click close to be forwarded to the dashboard list.";

            this.props.appStore!.addError(new Error(), errorMessage, oncloseerror);
        };

        if (this.state.dashboardName !== prevState.dashboardName) {
            await this.props.dashboardStore!.getDashboardById(this.state.dashboardName, undefined, callbackOnError);
            if (this.props.dashboardStore) {
                const dashboard = this.props.dashboardStore.dashboard;
                if (Number.isInteger(Number(this.props.dashboardName)) && dashboard) {
                    // replace current URL with the one that has the dashboard name
                    const url = Utils.getActionplanUrl(Utils.getUrlFriendlyString(dashboard.name), this.props.ticketId);
                    const params: Record<string, string> = {};
                    if (this.props.viewId) {
                        params["viewId"] = this.state.viewId as string;
                    }
                    if (this.props.refreshView) {
                        params.refreshView = this.state.refreshView as string;
                    }

                    const searchParams = new URLSearchParams(params);

                    this.props.navigate(url + "?" + searchParams.toString(), { replace: true });
                }
                if (dashboard && dashboard.dashboard_type === "BYODashboard") {
                    this.setState({ dashboard, dashboardId: dashboard.id, shouldEnableTicketCreation: true });
                    return null;
                }

                const queryParameters = parse(this.props.location.search);
                if (queryParameters.override_sheet) {
                    this.setState({ overrideSheet: queryParameters["override_sheet"] as string });
                    delete queryParameters.override_sheet;
                }
                // tab is an alias for override_sheet
                if (queryParameters.tab) {
                    this.setState({ overrideSheet: queryParameters["tab"] as string });
                    delete queryParameters.tab;
                }

                this.setState({ dashboard: this.props.dashboardStore.dashboard, dashboardId: dashboard?.id || 0, vizOptions: { ...tableauVizOptions }, queryParameters: { ...queryParameters } });
            }
        }
    }

    componentWillUnmount() {
        delete this.auth;
        this.props.dashboardStore!.resetDashboard();
        this.child = undefined;
    }

    refreshAndShow = () => {
        if (!this.props.dashboardStore!.dashboard) {
            return;
        }

        this.props.ticketStore!.getTicketsForDashboard(this.props.dashboardStore!.dashboard.id).then(() => {
            this.setState({ shouldShowActionplanForm: false });
        });
    };

    getExportId = (obj: any) => obj.ruuid;
    getExportLabel = (obj: any) => obj.sheet;

    handleExportChange = (next: any) => {
        this.setState({ selectedExport: next });
    };

    handleExportChangeType = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({ selectedExportType: event.target.value });
    };

    resizeHandler = (mouseDownEvent: MouseEvent<HTMLButtonElement>) => {
        const startSize = this.state.dashboardSize;
        const startPosition = mouseDownEvent.pageY;

        const onMouseMove = (mouseMoveEvent: any) => {
            this.setState({ dashboardSize: startSize - startPosition + mouseMoveEvent.pageY });
        };
        function onMouseUp() {
            document.body.removeEventListener("mousemove", onMouseMove);
            // uncomment the following line if not using `{ once: true }`
            // document.body.removeEventListener("mouseup", onMouseUp);
        }

        document.body.addEventListener("mousemove", onMouseMove);
        document.body.addEventListener("mouseup", onMouseUp, { once: true });
    };

    private _handleCancel = () => {
        this.setState({
            shouldShowActionplanForm: false,
        });
        window.scrollTo(0, 0);
    };

    private _getUpdatedData() {
        if (this.state.dashboard === undefined || !this.state.dashboard!.datasource || (!this.state.dashboard!.datasource.updated && !this.state.dashboard!.datasource.time_span)) {
            return "";
        }
        if (!this.state.dashboard!.datasource.time_span) {
            return "Data Updated " + Utils.getValidDateFromString(this.state.dashboard!.datasource!.updated, "MM/DD/YY h:mm A");
        } else {
            return "Data from " + this.state.dashboard!.datasource.time_span;
        }
    }

    private _getUpdatedDash() {
        if (!this.state.dashboard || !this.state.dashboard.published_date) {
            return "";
        }
        if (Utils.isSuperAdmin()) {
            return "Dashboard Updated " + Utils.getValidDateFromString(this.state.dashboard.published_date, "MM/DD/YY h:mm A");
        }
        return "";
    }

    getDashboardUrl = (dashboardName: string) => {
        if (DashboardQueries[dashboardName] && this.props.schoolSettings) {
            return `${Utils.getLinkBase()}/dashboard/${Utils.getUrlFriendlyString(dashboardName)}${DashboardQueries[dashboardName]?.replace(/60/g, this.props.schoolSettings.at_risk_threshold)}`;
        }
        return `${Utils.getLinkBase()}/dashboard/${Utils.getUrlFriendlyString(dashboardName)}`;
    };

    private _getDashboardItems() {
        const dashboards: JSX.Element[] = [];
        let first = true;
        _.forEach(this.props.dashboardStore!.dashboardsInCatgories, (dash, cat) => {
            if (first) {
                first = false;
            } else {
                dashboards.push(<NavDropdown.Divider key={`${cat}-divider`} />);
            }
            dashboards.push(<NavDropdown.Header key={cat}>{cat}</NavDropdown.Header>);
            dash.map((d: IDashboard) => {
                dashboards.push(
                    <Dropdown.Item as={NavLink} key={`${cat}_${d.id}`} data-test-id={`dashboard-item-${Utils.getUrlFriendlyString(d.name)}`} to={this.getDashboardUrl(d.name)} onClick={() => this._clearQueryParametersAndSetTitle(d.id, d.name)}>
                        {d.short_name}
                    </Dropdown.Item>
                );
            });
        });
        return dashboards;
    }

    private _clearQueryParametersAndSetTitle = (id: number, name: string) => {
        this.setState({ queryParameters: {} });
        Utils.setViewName("DashboardView-" + id, name);
    };

    private _filterExportList = (dashboardTab: string) => {
        this.props.dashboardStore!.dashboardExports;
        if (!this.props!.dashboardStore || !this.props.dashboardStore!.dashboardExports) {
            return [];
        }
        return this.props.dashboardStore.dashboardExports.filter((element) => element.tab === dashboardTab);
    };

    handleChildRef = (ref) => (this.child = ref);

    render() {
        return this.props.dashboardStore!.dashboard ? (
            <div className="dashboard-detail">
                <PageTitle pageTitle={this.state.dashboard!.name || "Dashboards"} />
                <Row className="section-action-bar">
                    <Col md={12} lg={5} className="title">
                        <Button variant="link" className="back-button" onClick={() => this.props.navigate(Utils.getLinkBase() + "/dashboards")}>
                            <ArrowLeft className="arrow-left" />
                        </Button>

                        <NavDropdown title={this.state.dashboard!.name} id={`NavDropDown_${this.state.dashboard!.name}`} className="dashboard-nav-dropdown" data-test-id="dashboard-nav-dropdown">
                            {this._getDashboardItems()}
                        </NavDropdown>
                    </Col>
                    <Col className="actions">
                        <Button
                            id="viewActionPlans"
                            data-test-id="dashboard-view-actionplans-btn"
                            className="view-action-plans"
                            disabled={!this.state.shouldEnableTicketCreation}
                            onClick={() => {
                                this.showLists();
                            }}
                        >
                            <Eye size={16} />
                            View ActionPlans
                        </Button>

                        <Dropdown className="dashboard-export-btn" show={this.state.showDropdown} onToggle={(isOpen) => this.setState({ showDropdown: isOpen })}>
                            <Dropdown.Toggle data-test-id="dashboard-export-btn" id="export" variant="outline" className={`export-btn ${this.state.showDropdown ? "active" : ""}`}>
                                <span className="d-flex align-items-center gap-2">
                                    Export
                                    {this.state.showDropdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </span>
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {/*<Dropdown.Item className="d-flex align-items-center px-2" onClick={() => this.handleExportDashboardPDF()}>*/}
                                {/*    <FileText className="me-2 text-secondary" size={20} />*/}
                                {/*    <span className="fw-normal text-dark text-uppercase">PDF</span>*/}
                                {/*</Dropdown.Item>*/}
                                {/*<Dropdown.Divider className="my-0" />*/}

                                <Dropdown.Item className="d-flex align-items-center px-2" onClick={() => this.handleExportDashboardPNG()}>
                                    <ImageIcon className="me-2 text-secondary" size={20} />
                                    <span className="fw-normal text-dark text-uppercase">PNG</span>
                                </Dropdown.Item>

                                {this.props.dashboardStore!.dashboardExports && this.props.dashboardStore!.dashboardExports.length > 0 && this.state.exportList.length > 0 ? (
                                    <>
                                        <Dropdown.Item className="d-flex align-items-center px-2" onClick={() => this.handleShowExportModal()} disabled={this.state.exportList.length === 0}>
                                            <Table className="me-2 text-secondary" size={20} />
                                            <span className="fw-normal text-dark text-capitalize">Source Table</span>
                                        </Dropdown.Item>

                                        <Dropdown.Divider className="my-0" />
                                    </>
                                ) : null}

                                {this.props.featureGatingStore && Utils.isFeatureActive("api", "data_export", this.props.featureGatingStore.featureGatingList) ? (
                                    <Dropdown.Item as={"button"} className="d-flex align-items-center px-2 border-0" onClick={() => this.exportActionPlans()}>
                                        <Download className="me-2 text-secondary" size={20} />
                                        <span className="fw-normal text-dark">Export ActionPlans</span>
                                    </Dropdown.Item>
                                ) : null}
                            </Dropdown.Menu>
                        </Dropdown>

                        <Button
                            id="createTaskButton"
                            data-test-id="dashboard-create-actionplan-btn"
                            disabled={!this.state.shouldEnableTicketCreation}
                            onClick={() => {
                                this.handleShowSnapshotModal();
                            }}
                        >
                            <Plus />
                            Create ActionPlan
                        </Button>
                    </Col>
                </Row>
                {this.state.dashboard && this.state.dashboard.dashboard_type === "BYODashboard" && !this.state.dashboard.use_url && (
                    <>
                        <iframe className="w-full resources" style={{ height: this.state.dashboardSize + "px" }} title="Dashboard Content" srcDoc={this.state.dashboard.embedded_code}></iframe>
                        <div className="text-center">
                            <Button variant="icon" data-test-id="dashboard-resize-btn" onMouseDown={this.resizeHandler}>
                                <VerticalResize />
                            </Button>
                        </div>
                    </>
                )}

                {this.state.dashboard && this.state.dashboard.dashboard_type === "BYODashboard" && this.state.dashboard.use_url && (
                    <>
                        <iframe id="byoc-url" style={{ height: this.state.dashboardSize + "px" }} className="w-full resources" src={this.state.dashboard.url} title="BYO Dashboard"></iframe>
                        <div className="text-center">
                            <Button variant="icon" data-test-id="dashboard-resize-btn" onMouseDown={this.resizeHandler}>
                                <VerticalResize />
                            </Button>
                        </div>
                    </>
                )}
                {this.state.dashboard && this.state.dashboard.dashboard_type === "enflux_dashboard" && (
                    <>
                        <TableauDisplay
                            onRef={this.handleChildRef}
                            dashboardId={this.state.dashboardId}
                            viewId={this.state.viewId}
                            refreshView={this.state.refreshView}
                            vizOptions={this.state.vizOptions}
                            queryParameters={this.state.queryParameters}
                            handleLoaded={this.handleVizLoaded}
                            handleTabSwitch={this.handleTabSwitch}
                            shouldSaveView={this.state.shouldSaveView}
                            style={{ margin: "20px auto", minHeight: 550, height: this.state.dashboardSize + "px", width: "100%" }}
                            overrideSheet={this.state.overrideSheet}
                            isResponsive={this.state.dashboard.is_responsive}
                        />
                        {this.state.dashboard.is_responsive && (
                            <div className="text-center">
                                <Button variant="icon" data-test-id="dashboard-resize-btn" onMouseDown={this.resizeHandler}>
                                    <VerticalResize />
                                </Button>
                            </div>
                        )}
                    </>
                )}
                <div className="dashboardUpdated">{this._getUpdatedData()}</div>
                <div className="dashboardUpdated">{this._getUpdatedDash()}</div>
                <div className="dashboard-list-wrapper">
                    {this.state.shouldShowActionplanForm ? (
                        <TicketDetailForm formRef={this.ticketDetailFormRef} dashboard={this.props.dashboardStore!.dashboard} setFollowUpTo={this.state.taskToFollowUp} isPreview={false} shouldShowSnapshotWarning={this.state.shouldShowSnapshotModal} onCancel={this._handleCancel} onSaveDone={this.refreshAndShow} />
                    ) : (
                        <TicketList dashboard={this.props.dashboardStore!.dashboard} formRef={this.ticketListFormRef} dashboardId={this.state.dashboardId} refreshView={this.state.refreshView} instaOpenId={this.state.ticketId} navigate={this.props.navigate} location={this.props.location} isReadyToAddComment={this.state.shouldEnableTicketCreation} hasToExpandActionPlan={true} />
                    )}
                </div>
                <Modal show={this.state.shouldShowExportModal}>
                    <Modal.Header>
                        <Modal.Title className="fw-bold" style={{ lineHeight: "36px", fontSize: "24px" }}>
                            Data Export
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Choose View:
                        <SelectWrapper name="export-select" options={this.state.exportList} getOptionLabel={this.getExportLabel} getOptionValue={this.getExportId} onChange={this.handleExportChange} value={this.state.selectedExport} />
                        <p className="my-2">Choose Format</p>
                        <Form.Check inline onChange={this.handleExportChangeType} checked={this.state.selectedExportType === "excel"} className="input-small" value="excel" type="radio" name="exportFormat" label="Excel" id="csv-format" />
                        <Form.Check inline className="input-small ms-5" onChange={this.handleExportChangeType} checked={this.state.selectedExportType === "csv"} value="csv" type="radio" name="exportFormat" label="CSV" id="excel-format" />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" style={{ width: "47%" }} onClick={this.handleCloseExportModal} id="closeExportButton">
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={this.downloadExport} style={{ marginLeft: 15, width: "46%" }} id="downloadExportButton">
                            Download
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        ) : null;
    }
}
